import React, { useState, useLayoutEffect, useMemo } from "react";
import cn from "classnames";
import style from "./common.module.scss";
import * as am5 from "@amcharts/amcharts5";
import * as am5xy from "@amcharts/amcharts5/xy";
import am5themes_Animated from "@amcharts/amcharts5/themes/Animated";
import { useScreenSize } from "../../../hooks/useScreenSize";
import {
  formatDateWithDay,
  getAmChartsLocale,
  processTransportData,
  shouldShowYear,
} from "../../../utils/chartDataProcessors";
import { useLanguage } from "../../../context/LanguageContext";
import { useTranslation } from "react-i18next";
import GroupByDropdown from "./GroupByDropdown/GroupByDropdown";

const AnalyticsByTransportType = ({ item }) => {
  const { t } = useTranslation();
  const { isMobileView } = useScreenSize();
  const { currentLanguage } = useLanguage();
  const [groupBy, setGroupBy] = useState("day");

  const processedData = useMemo(() => {
    if (groupBy === "day") {
      const showYear = shouldShowYear(item);

      return item
        .map((dataItem) => {
          const [day, month, year] = dataItem.date.split(".");
          const isoDate = new Date(year, month - 1, day);

          return {
            ...dataItem,
            month: formatDateWithDay(isoDate, currentLanguage, showYear),
            tooltipText: formatDateWithDay(isoDate, currentLanguage, showYear).replace("\n", " "),
            isoDate,
          };
        })
        .sort((a, b) => a.isoDate - b.isoDate);
    } else {
      return processTransportData(item, currentLanguage);
    }
  }, [item, currentLanguage, groupBy]);

  useLayoutEffect(() => {
    let root = am5.Root.new("chartdiv-transport");
    root.setThemes([am5themes_Animated.new(root)]);

    const locale = getAmChartsLocale(currentLanguage);
    if (locale) {
      root.locale = locale;
    }

    root._logo.dispose();

    let chart = root.container.children.push(
      am5xy.XYChart.new(root, {
        layout: root.verticalLayout,
        wheelY: "zoomX",
      }),
    );

    if (isMobileView) {
      chart.set("paddingLeft", 1);
    }
    chart.set("wheelable", true);

    const cursor = chart.set(
      "cursor",
      am5xy.XYCursor.new(root, {
        behavior: "zoomX",
      }),
    );
    cursor.lineY.set("visible", false);

    let xAxis = chart.xAxes.push(
      am5xy.CategoryAxis.new(root, {
        categoryField: "month",
        groupData: true,
        renderer: am5xy.AxisRendererX.new(root, {
          minGridDistance: groupBy === "day" ? 60 : 40,
          cellStartLocation: 0.1,
          cellEndLocation: 0.9,
        }),
        tooltip: am5.Tooltip.new(root, {
          labelText: groupBy === "day" ? "{tooltipText}" : "{month}",
        }),
      }),
    );
    xAxis.data.setAll(processedData);

    xAxis.get("renderer").grid.template.setAll({
      strokeDasharray: [4, 4],
      strokeOpacity: 1,
      stroke: am5.color(0xe7e7f4),
    });

    if (groupBy === "day") {
      xAxis.get("renderer").labels.template.setAll({
        fill: am5.color("#8E8E8E"),
        fontSize: 12,
        rotation: 0,
        multiline: true,
        textAlign: "center",
        centerX: am5.percent(50),
        maxWidth: 70,
        oversizedBehavior: "wrap",
      });
    } else {
      xAxis.get("renderer").labels.template.setAll({
        fill: am5.color("#8E8E8E"),
        fontSize: 12,
        rotation: 0,
        multiline: true,
        textAlign: "center",
        centerX: am5.percent(50),
        maxWidth: 50,
        oversizedBehavior: "wrap",
      });
    }

    let yAxis = chart.yAxes.push(
      am5xy.ValueAxis.new(root, {
        renderer: am5xy.AxisRendererY.new(root, {}),
      }),
    );

    yAxis.get("renderer").grid.template.setAll({
      strokeDasharray: [4, 4],
      strokeOpacity: 1,
      stroke: am5.color(0xe7e7f4),
    });

    yAxis.get("renderer").labels.template.setAll({
      fill: am5.color("#8E8E8E"),
      fontSize: 12,
    });

    const colors = {
      awning: am5.color(0x22c0ff),
      covered: am5.color(0xbeacff),
      isothermal: am5.color(0xffb5ce),
      refrigerator: am5.color(0x4e7cff),
    };

    const createSeries = (name, field, color) => {
      let series = chart.series.push(
        am5xy.ColumnSeries.new(root, {
          name: name,
          xAxis: xAxis,
          yAxis: yAxis,
          categoryXField: "month",
          valueYField: field,
          stacked: true,
        }),
      );

      series.columns.template.setAll({
        // cornerRadiusTL: 16,
        // cornerRadiusTR: 16,
        fill: color,
        stroke: color,
        maxWidth: 80,
      });

      series.set(
        "tooltip",
        am5.Tooltip.new(root, {
          labelText: "{name}: {valueY}",
        }),
      );

      series.data.setAll(processedData);
    };

    createSeries(t("analytics.refrigerator"), "refrigerator", colors.refrigerator);
    createSeries(t("analytics.tent"), "awning", colors.awning);
    createSeries(t("analytics.covered"), "covered", colors.covered);
    createSeries(t("analytics.isotherm"), "isothermal", colors.isothermal);

    return () => {
      root.dispose();
    };
  }, [item, isMobileView, processedData, currentLanguage, groupBy]);

  return (
    <div className={style.content}>
      <div className={style.filter}>
        <span className={style.filter_title}>{t("analytics.transportType")}</span>
        <GroupByDropdown groupBy={groupBy} setGroupBy={setGroupBy} />
      </div>

      <div className={cn(style.area__namesMobile)}>
        <div style={{ display: "flex", gap: "4px", alignItems: "center" }}>
          <div className={style.area_icon} style={{ backgroundColor: "#4E7CFF" }}></div>
          <div className={style.area_type}>{t("analytics.refrigerator")}</div>
        </div>
        <div style={{ display: "flex", gap: "4px", alignItems: "center" }}>
          <div className={style.area_icon} style={{ backgroundColor: "#22C0FF" }}></div>
          <div className={style.area_type}>{t("analytics.tent")}</div>
        </div>
        <div style={{ display: "flex", gap: "4px", alignItems: "center" }}>
          <div className={style.area_icon} style={{ backgroundColor: "#beacff" }}></div>
          <div className={style.area_type}>{t("analytics.covered")}</div>
        </div>
        <div style={{ display: "flex", gap: "4px", alignItems: "center" }}>
          <div className={style.area_icon} style={{ backgroundColor: "#FFB5CE" }}></div>
          <div className={style.area_type}>{t("analytics.isotherm")}</div>
        </div>
      </div>

      <div className={style.area}>
        <div id="chartdiv-transport" className={style.area_chart}></div>
        <div className={style.area_names}>
          <div style={{ display: "flex", gap: "4px", alignItems: "center" }}>
            <div className={style.area_icon} style={{ backgroundColor: "#4E7CFF" }}></div>
            <div className={style.area_type}>{t("analytics.refrigerator")}</div>
          </div>
          <div style={{ display: "flex", gap: "4px", alignItems: "center" }}>
            <div className={style.area_icon} style={{ backgroundColor: "#22C0FF" }}></div>
            <div className={style.area_type}>{t("analytics.tent")}</div>
          </div>
          <div style={{ display: "flex", gap: "4px", alignItems: "center" }}>
            <div className={style.area_icon} style={{ backgroundColor: "#beacff" }}></div>
            <div className={style.area_type}>{t("analytics.covered")}</div>
          </div>
          <div style={{ display: "flex", gap: "4px", alignItems: "center" }}>
            <div className={style.area_icon} style={{ backgroundColor: "#FFB5CE" }}></div>
            <div className={style.area_type}>{t("analytics.isotherm")}</div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default AnalyticsByTransportType;
